/* ─────────────────────────────────────────────────────────────────────────
 * Amour — the wall.
 *
 * Forty frames — neon studio silhouettes, one strong colour each. That last
 * part is doing real work: the mosaic prints them at a fifteenth of the
 * screen, and at that size a photograph is its colour and its outline and
 * nothing else. A set of naturally-lit portraits turns to mud here.
 *
 * The heart lattice asks for about forty-seven cells on a desktop, so a few
 * frames repeat. They are dealt in ROW ORDER, which puts every repeat as far
 * from its twin as the shape allows — the surplus lands in the point, and its
 * originals are up in the lobes.
 * ───────────────────────────────────────────────────────────────────────── */

/** Served from a CDN, one-year immutable. This may be a different origin than
 *  the site: the section never reads pixels back off the canvas, so no CORS
 *  grant and no `crossOrigin` on the Image is involved. Point it at your own
 *  host and keep the `pNN.jpg` convention to use your own shoot. */
export const IMG_BASE =
  "https://pub-dc2abe5caae44240a6d1913566a75e0f.r2.dev/sections/amour/";

/** `pNN.jpg`, 1-indexed. Swap the files to use your own shoot; nothing in the
 *  section reads a filename, only the count — but META must stay the same
 *  length, since entry `i` is the credit for `PHOTOS[i]`. */
export const PHOTOS: string[] = Array.from(
  { length: 40 },
  (_, i) => `p${String(i + 1).padStart(2, "0")}.jpg`
);

/** What the detail card shows when a photograph is opened. One entry per
 *  frame, in the same order as PHOTOS — index `i` describes `PHOTOS[i]`. */
export interface PhotoMeta {
  name: string;
  tags: string[];
}

/* Forty sitters, one per frame. The tag sets are deliberately SHORT and drawn
 * from a small vocabulary — three chips is what the card holds at its width,
 * and a portfolio's tags are a controlled list rather than free text. The
 * middle chip is always the frame's dominant COLOUR, which is the one thing
 * you can verify against the photograph at a glance. */
export const META: PhotoMeta[] = [
  { name: "Ludita Goyongo", tags: ["Silhouette", "Gel", "Scarlet"] },
  { name: "Mira Solenne", tags: ["Profile", "Studio", "Cobalt"] },
  { name: "Aurelio Vance", tags: ["Braid", "Rim Light", "Magenta"] },
  { name: "Nia Okonjo", tags: ["Hands", "Violet", "Studio"] },
  { name: "Teodor Brandt", tags: ["Hat", "Silhouette", "Teal"] },
  { name: "Selin Aydar", tags: ["Dancer", "Acid", "Motion"] },
  { name: "Rue Delacroix", tags: ["Gesture", "Amber", "Contour"] },
  { name: "Osei Mensah", tags: ["Profile", "Crimson", "Halo"] },
  { name: "Ilke Norberg", tags: ["Hair", "Tangerine", "Sweep"] },
  { name: "Cassia Moreau", tags: ["Hush", "Indigo", "Close"] },
  { name: "Dane Whitlock", tags: ["Hands", "Emerald", "Cross"] },
  { name: "Priya Raman", tags: ["Eyes", "Shadow", "Slate"] },
  { name: "Sacha Bellini", tags: ["Hoops", "Duotone", "Ochre"] },
  { name: "Kofi Adjei", tags: ["Palm", "Cobalt", "Press"] },
  { name: "Wren Halloway", tags: ["Dancer", "Scarlet", "Fold"] },
  { name: "Anouk Ferrer", tags: ["Profile", "Fuchsia", "Curl"] },
  { name: "Emre Dogan", tags: ["Chignon", "Emerald", "Lift"] },
  { name: "Talia Rosen", tags: ["Breath", "Indigo", "Smoke"] },
  { name: "Bodhi Lane", tags: ["Hands", "Amber", "Hold"] },
  { name: "Isolde Marchetti", tags: ["Reach", "Cyan", "Pair"] },
  { name: "Cyrus Ndiaye", tags: ["Drape", "Violet", "Seated"] },
  { name: "Freya Lindqvist", tags: ["Mask", "Crimson", "Hands"] },
  { name: "Rafael Duarte", tags: ["Profile", "Tangerine", "Nape"] },
  { name: "Noor Haddad", tags: ["Reach", "Emerald", "Two"] },
  { name: "Juno Castellan", tags: ["Curls", "Teal", "Turn"] },
  { name: "Ezra Fontaine", tags: ["Seated", "Rose", "Arch"] },
  { name: "Lila Anand", tags: ["Ruff", "Scarlet", "Gold"] },
  { name: "Marlow Reyes", tags: ["Prism", "Violet", "Beam"] },
  { name: "Sana Ito", tags: ["Pair", "Indigo", "Mirror"] },
  { name: "Otto Brenner", tags: ["Arm", "Acid", "Frame"] },
  { name: "Vega Almeida", tags: ["Bob", "Scarlet", "Shoulder"] },
  { name: "Idris Kane", tags: ["Back", "Cerulean", "Arms"] },
  { name: "Amias Cole", tags: ["Dancer", "Magenta", "Arabesque"] },
  { name: "Roza Petrova", tags: ["Eye", "Emerald", "Macro"] },
  { name: "Hugo Marchand", tags: ["Palms", "Crimson", "Steeple"] },
  { name: "Zuri Abara", tags: ["Hair", "Indigo", "Wind"] },
  { name: "Elin Vasquez", tags: ["Seated", "Tangerine", "Coil"] },
  { name: "Caspian Rho", tags: ["Face", "Acid", "Split"] },
  { name: "Mireille Sand", tags: ["Hands", "Violet", "Veil"] },
  { name: "Jonas Falk", tags: ["Arms", "Amber", "Rise"] },
];

export const STUDIO = {
  /** Sits in the header bar, centred. A placeholder studio — every string in
   *  this object is meant to be replaced by whoever buys the section. */
  mark: "AMOUR",
  /** The split headline that frames the heart, left and right of the stage. */
  /* The headline is ONE SENTENCE, split around the heart and read left to
   * right: "Everything we shot / adds up to one thing." It is written to
   * describe what the composition is doing rather than to sit next to it —
   * fifty separate frames resolving into a single shape is the whole idea, so
   * the words say that and then stop. The smaller line of each pair is the
   * setup and the larger is the payoff, which is why they are not the same
   * size: read at a glance, only the two big lines land, and those two alone
   * still make a sentence. */
  headLeft: ["Everything", "we shot"],
  headRight: ["adds up to", "one thing"],
  /** One line under the heart. */
  note: "A decade of work, in one shape —",
  noteAccent: "see the archive",
  /** Detail-card chrome. */
  collect: "Add to Collection",
  hint: "on the keyboard to move",
};
